// Route tree + router (B5B §2.3 beforeLoad gate). Code-based routing (C4.0-D5). PLAN-C1.11.
import { createRootRouteWithContext, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { hasPermission, type SympllexSession } from "@sympllex/auth";
import { RootLayout } from "./routes/root";
import { DashboardRoute } from "./routes/dashboard";
import { PortfolioRoute } from "./routes/portfolio";
import { InboxRoute } from "./routes/inbox";
import { TenantDetailRoute } from "./routes/tenant-detail";
import { TicketDetailRoute } from "./routes/ticket-detail";
import { VerwerkingDetailRoute } from "./routes/verwerking-detail";
import { ProgramDetailRoute } from "./routes/program-detail";
import { SearchRoute } from "./routes/search";
import { ProgramsRoute } from "./routes/programs";
import { FirmSettingsRoute } from "./routes/firm-settings";
import { EngagementsRoute } from "./routes/engagements";
import { ForbiddenRoute } from "./routes/forbidden";
import { DEV_SESSION } from "./lib/session";

export interface RouterContext { session: SympllexSession }

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: RootLayout });

// Home = the cross-tenant portfolio dashboard (B5B-PARTNER-COCKPIT §2). The older single-engagement
// dashboard stays at /dashboard.
const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: PortfolioRoute });
const inboxRoute = createRoute({ getParentRoute: () => rootRoute, path: "/inbox", component: InboxRoute });
const tenantDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/tenant/$tenantId", component: TenantDetailRoute });
const ticketDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/ticket/$ticketId", component: TicketDetailRoute });
const verwerkingDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/verwerking/$verwerkingId", component: VerwerkingDetailRoute });
const programDetailRoute = createRoute({ getParentRoute: () => rootRoute, path: "/program/$programId", component: ProgramDetailRoute });
const searchRoute = createRoute({ getParentRoute: () => rootRoute, path: "/search", component: SearchRoute });
const programsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/programs", component: ProgramsRoute });
const firmSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: FirmSettingsRoute });
const dashboardRoute = createRoute({ getParentRoute: () => rootRoute, path: "/dashboard", component: DashboardRoute });

const engagementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engagements",
  beforeLoad: ({ context }) => {
    if (!hasPermission(context.session, "partner_relationship.read")) throw redirect({ to: "/forbidden" });
  },
  component: EngagementsRoute,
});

const forbiddenRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forbidden", component: ForbiddenRoute });

const routeTree = rootRoute.addChildren([indexRoute, inboxRoute, tenantDetailRoute, ticketDetailRoute, verwerkingDetailRoute, programDetailRoute, searchRoute, programsRoute, firmSettingsRoute, dashboardRoute, engagementsRoute, forbiddenRoute]);

export const router = createRouter({
  routeTree,
  context: { session: DEV_SESSION },
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register { router: typeof router }
}
